import NextLink from 'next/link'

/**
 * SmartLink — the ONE link primitive the VRS UI is allowed to use.
 *
 * Raw `next/link` is banned in app/ and components/ (see the eslint
 * `local-rules/no-raw-next-link` rule) — import this instead.
 *
 * WHY THIS EXISTS
 * ---------------
 * The SSR HTML for every page is cached in the parent server process (~0.1s to
 * serve, NO render worker involved). App Router `<Link>` soft-navigation bypasses
 * that cache — it fetches the RSC "flight" payload, which is uncacheable and
 * always hits the single render worker — and by default it PREFETCHES that flight
 * for every visible link, firing a burst of RSC requests on every page load that
 * saturates the one worker and 502s real traffic.
 *
 * So the DEFAULT here is a hard navigation (`<a>`) that reuses the cached SSR:
 * fast, zero worker load, no RSC, no prefetch storm. Soft (client-side) nav is an
 * explicit opt-in for the few flows that need it; prefetch is an even rarer one.
 *
 * MODES
 * -----
 *   <SmartLink href="/x">…</SmartLink>
 *       DEFAULT — plain <a>, HARD navigation → served from the cached SSR, zero
 *       worker load, no RSC. Use for ~all links (nav, footer, cards, cross-links).
 *
 *   <SmartLink href="/x" soft>…</SmartLink>
 *       Next <Link prefetch={false}> — client SOFT nav on CLICK only (no
 *       speculative prefetch). Use only where a full reload would lose in-progress
 *       client state (e.g. steps within an interactive flow).
 *
 *   <SmartLink href="/x" soft prefetch>…</SmartLink>
 *       Next <Link prefetch> — soft nav WITH prefetch. RARE — reserve for the few
 *       surfaces that must feel instant. Prefetch is a deliberate, per-link opt-in.
 *
 * Notes:
 *  - This is a plain (server-compatible) component: no hooks, no 'use client'. It
 *    can be rendered from Server or Client components. `<Link>` (a client boundary)
 *    is only instantiated in the soft path.
 *  - HARD mode expects a string `href` (an internal path like "/us/product/x" or an
 *    absolute URL). Object hrefs (Next route objects) must use `soft` (Next <Link>
 *    resolves them) or be pre-stringified by the caller.
 *  - All extra props (className, aria-*, onClick, target, rel, …) forward to the
 *    underlying element.
 */
export default function SmartLink({ href, soft = false, prefetch = false, children, ...rest }) {
  if (!soft) {
    // Hard navigation (default): reuse the cached SSR, no worker, no RSC/prefetch.
    return (
      <a href={href} {...rest}>
        {children}
      </a>
    )
  }
  // Soft (client-side) navigation. prefetch defaults OFF; opt in explicitly.
  return (
    <NextLink href={href} prefetch={prefetch} {...rest}>
      {children}
    </NextLink>
  )
}
